// Hand the browser a file to save.
//
// The one browser-only step in the export path — everything that builds the
// bytes is pure and lives in core. Kept separate from printReport for the same
// reason: printing and saving fail in different ways and neither should be able
// to break the other.
//
// ⚠️ IT CANNOT WORK INSIDE THE APP. A Capacitor webview has no download manager:
// `<a download>` on a blob URL resolves to nothing at all — no file, no error,
// no dialog. The desktop shell normally implies a browser, but NOT always:
// `localStorage.uiMode = 'desktop'` overrides the native check (see
// core/platform.detectUiMode), so a Super Admin on a tablet can reach this page
// inside the app. Rather than let the tap do nothing, this throws a typed error
// the caller turns into a sentence. Saving from the app would need
// @capacitor/filesystem + @capacitor/share, neither of which is installed.

import { isNativePlatform } from '../../core/platform';

export const NATIVE_DOWNLOAD_UNSUPPORTED = 'NATIVE_DOWNLOAD_UNSUPPORTED';

export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Save `bytes` as `filename`.
 *
 * Throws `NATIVE_DOWNLOAD_UNSUPPORTED` inside the native app — see above.
 */
export function downloadBytes(filename, bytes, mime) {
  if (isNativePlatform()) {
    const err = new Error('Downloads are not available inside the app.');
    err.code = NATIVE_DOWNLOAD_UNSUPPORTED;
    throw err;
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  // Appended rather than clicked detached: Firefox ignores a click on an anchor
  // that is not in the document, and ignores it silently.
  document.body.appendChild(a);
  a.click();

  // ⚠️ NOT REVOKED SYNCHRONOUSLY. The click only STARTS the save; revoking the
  // URL in the same tick pulls the bytes out from under it and Firefox writes a
  // zero-byte file. A second is far longer than a handful of kilobytes needs and
  // costs nothing but the blob's memory until then.
  setTimeout(() => {
    URL.revokeObjectURL(url);
    if (a.parentNode) a.parentNode.removeChild(a);
  }, 1000);
}

export default downloadBytes;
